/**
 * Auth context and hook for managing authentication state.
 * SDD §7.2 — hooks/useAuth.js
 *
 * Provides:
 * - Current user state (loading / authenticated / unauthenticated)
 * - Transparent re-auth on 401 (FR-AUTH-07)
 * - Logout function
 */

import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { fetchCurrentUser, logout as doLogout, type AuthUser } from '../lib/auth';
import { registerPushNotifications } from '../lib/pushNotifications';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const pushRegistered = useRef(false);

  async function refreshUser() {
    try {
      const currentUser = await fetchCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshUser();
  }, []);

  // Register for push notifications once user is authenticated
  useEffect(() => {
    if (user && !pushRegistered.current) {
      pushRegistered.current = true;
      registerPushNotifications().catch(() => {
        // Push registration is best-effort
      });
    }
  }, [user]);

  const value: AuthContextValue = {
    user,
    loading,
    logout: doLogout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
